import TimelineService from '@/services/TimelineService'
import { scrollToElement, sortRatingBeforeFeedback, scrollToLastPosition } from '@/lib/utils'
import moment from 'moment-timezone'
import { dateMapper } from '@/lib/date'
import { throttle } from 'lodash-es'
import router from '@/router'
import { ticketPlaceholderId } from '@/lib/ticket-created-message'

const throttleTimelineEvents = throttle(
  async (dispatch) => {
    dispatch('fetchTimeline')
  },
  1000,
  { trailing: false }
)

// A websocket `sent-gift-card` event carries `giftCard` as a bare id: the card lives in
// the gift-card subdomain, and only the timeline HTTP endpoint hydrates it (see
// hydrateGiftCards in OBS timeline.controller). Rendering an unhydrated entry shows
// "A gift card ending in  was sent" until a later refetch fills it in, so the entry is
// refetched instead of rendered. Leading + trailing: a second card issued inside the
// window still gets a fetch that can see it.
const throttleGiftCardHydration = throttle((dispatch) => {
  dispatch('fetchTimeline', { latest: true })
}, 1000)

// Whether a card's entry carries the card itself rather than just its id.
function isGiftCardEventHydrated(event) {
  return !!event?.giftCard?.last4Digits
}

const ACTIONABLE_EVENTS = [
  'internal-note-added',
  'left-feedback',
  'left-survey',
  'incoming-message',
  'outgoing-message'
]

// How long after a card's entry an outgoing message still counts as the reply that
// introduced it. Issuance and the reply are two requests from the same click, so the
// real gap is under a couple of seconds; a minute is slack for a slow issue call.
const GIFT_CARD_REPLY_WINDOW_SECONDS = 60

// How long a card's entry waits for the reply's real event before it is committed
// anyway. Only reached when that event never arrives (dropped websocket message) —
// the entry must still show up rather than being held forever.
const GIFT_CARD_HOLD_TIMEOUT_MS = 10000

// One pending flush timer per conversation, keyed by id. Module scope, not state:
// a timer handle is not data any component reads.
const giftCardHoldTimers = {}

// Cleared alongside a timeline reset — an armed timer left over from the
// conversation being left behind would otherwise flush early for whatever
// gets held next in the same conversation id.
function clearAllGiftCardHoldTimers() {
  Object.keys(giftCardHoldTimers).forEach((conversationId) => {
    clearTimeout(giftCardHoldTimers[conversationId])
    delete giftCardHoldTimers[conversationId]
  })
}

// The record each kind of optimistic event stands in for. A placeholder is superseded
// once that record shows up in the timeline under its own analytic event.
const TEMP_EVENT_RECORD_KEYS = {
  'outgoing-message': 'message',
  'internal-note-added': 'note'
}

// The ticket-note-writer's note normally lands in 2-4s; past this it failed or its websocket
// event was dropped.
export const TICKET_PLACEHOLDER_TIMEOUT_MS = 30000

const ticketPlaceholderTimers = {}

function clearTicketPlaceholderTimer(eventId) {
  if (!ticketPlaceholderTimers[eventId]) return
  clearTimeout(ticketPlaceholderTimers[eventId])
  delete ticketPlaceholderTimers[eventId]
}

const noteTicketId = (event) => event?.note?.ticket?.id

const hasRealTicketNote = (events, ticketId) =>
  events.some(
    (event) =>
      event.type === 'internal-note-added' &&
      !event.isMessagePlaceholder &&
      noteTicketId(event) === ticketId
  )

// A real ticket note replaces the placeholder for its ticket, whichever path brought it in.
function clearTicketPlaceholderFor(commit, getters, event, conversationId) {
  const eventId = ticketPlaceholderId(noteTicketId(event))
  clearTicketPlaceholderTimer(eventId)
  if (getters.selectTempEventIds(conversationId).includes(eventId)) {
    commit('CLEAR_TEMP_EVENT', { conversationId, eventId })
  }
}

// Whether a real event has already brought in the record a placeholder stands for.
function isSupersededTempEvent(tempEvent, events) {
  // A ticket placeholder has no note id of its own, so it is matched by ticket.
  const placeholderTicketId = tempEvent.type === 'internal-note-added' && noteTicketId(tempEvent)
  if (placeholderTicketId && tempEvent._id === ticketPlaceholderId(placeholderTicketId)) {
    return hasRealTicketNote(events, placeholderTicketId)
  }

  const recordKey = TEMP_EVENT_RECORD_KEYS[tempEvent.type]
  const recordId = recordKey && tempEvent[recordKey]?._id
  if (!recordId) return false

  return events.some((event) => event.type === tempEvent.type && event[recordKey]?._id === recordId)
}

/**
 * Moves a `sent-gift-card` entry below the reply that introduced it — the card is
 * written server-side before the reply, so a strict `created` sort renders it
 * backwards from what the guest was told. Only swaps with the outgoing message
 * directly after it, within the window.
 */
function orderGiftCardsAfterTheirReply(events) {
  const ordered = [...events]

  for (let index = 0; index < ordered.length; index += 1) {
    if (ordered[index].type !== 'sent-gift-card') continue

    const card = ordered[index]
    const next = ordered[index + 1]
    if (!next || next.type !== 'outgoing-message') continue

    // A single bounded swap, not a chase: a second reply sent shortly after the
    // first is not the reply that introduced the card and must stay where it sorts.
    const secondsAfterCard = moment(next.created).diff(moment(card.created), 'seconds', true)
    if (secondsAfterCard < 0 || secondsAfterCard > GIFT_CARD_REPLY_WINDOW_SECONDS) continue

    ordered[index] = next
    ordered[index + 1] = card
    // The card now sits at index + 1; skip it so the outer loop doesn't treat it
    // as a fresh card and chase a second, unrelated reply past it too.
    index += 1
  }

  return ordered
}

const defaultState = () => ({
  showTimeline: false,
  lastEventId: null,
  events: [],
  eventsById: {},
  tempEvents: {},
  timelineEvents: [],
  loyalties: [],
  messages: [],
  orders: [],
  surveys: [],
  allPreviousEventsLoaded: false,
  tags: [],
  messageLimit: 30,
  timelineLoading: false,
  isWithinCelebrationTime: false,
  // Gift-card entries held back while the reply that introduces them is still
  // optimistic, keyed by conversation id. See holdGiftCardEvents.
  pendingGiftCardEvents: {},
  // Set the moment a gift card is issued, before sendReply's placeholder exists —
  // closes the race where a websocket card event lands in the gap between the
  // issue call resolving and the reply's optimistic event being committed.
  expectingReply: {}
})

export default {
  namespaced: true,
  state: {
    ...defaultState()
  },
  getters: {
    selectMessageLimit: (state) => state.messageLimit,
    selectAllPreviousEventsLoaded: (state) => state.allPreviousEventsLoaded,
    selectFirstEventId: (state) => (state.events.length ? state.events[0]._id : null),
    selectLastEventId: (state) => {
      // Find the last event that will actually be rendered
      // Skip survey events without ratings since they won't be rendered
      if (!state.events || !Array.isArray(state.events) || state.events.length === 0) {
        return null
      }
      for (let i = state.events.length - 1; i >= 0; i--) {
        const event = state.events[i]
        if (!event || !event._id) {
          continue
        }
        // Survey events without ratings are not rendered
        if (event.type === 'left-survey' && (!event.survey || !event.survey.rating)) {
          continue
        }
        return event._id
      }
      return null
    },
    selectLastEvent: (state) => state.events[state.events.length - 1],
    selectLastActionableEvent: (state) => {
      const actionableEvents = state.events.filter((e) => ACTIONABLE_EVENTS.includes(e.type))
      return actionableEvents.length ? actionableEvents[actionableEvents.length - 1] : null
    },
    selectTimelineEvents: (state) => state.timelineEvents,
    selectIsWithinCelebrationTime: (state) => state.isWithinCelebrationTime,
    selectTimelineLoading: (state) => state.timelineLoading,
    selectTempEventIds: (state) => (conversationId) =>
      Object.keys(state.tempEvents[conversationId] || {}),
    selectTempEvents: (state) => (conversationId) =>
      Object.values(state.tempEvents[conversationId] || {}),
    selectTempEventById: (state, getters, rootState, rootGetters) => (id) =>
      state.tempEvents?.[rootGetters['conversationV2/selectActiveConversation']?._id]?.[id],
    selectEventById: (state) => (id) => state.eventsById[id],
    // Optimistic events whose saved record hasn't loaded yet. CLEAR_TEMP_EVENT only
    // fires on paths that recognize the arriving event, so this supersedes by record
    // id instead — a placeholder that outlives its record renders the message twice.
    selectVisibleTempEvents: (state, getters) => (conversationId) =>
      getters
        .selectTempEvents(conversationId)
        .filter((tempEvent) => !isSupersededTempEvent(tempEvent, state.events)),
    selectPendingGiftCardEvents: (state) => (conversationId) =>
      state.pendingGiftCardEvents[conversationId] || [],
    // True while the operator's own reply is still only an optimistic event, which
    // renders after every real one — so anything committed now shows above it.
    selectHasOptimisticReply: (state, getters) => (conversationId) =>
      getters
        .selectVisibleTempEvents(conversationId)
        .some((event) => event.type === 'outgoing-message'),
    // True from the moment a gift card is issued until its reply either lands as an
    // optimistic event or the send flow gives up — covers the gap selectHasOptimisticReply
    // can't see on its own.
    selectIsExpectingReply: (state) => (conversationId) => !!state.expectingReply[conversationId]
  },
  mutations: {
    RESET_TIMELINE_DATA(state) {
      state.events = []
      state.loyalties = []
      state.messages = []
      state.orders = []
      state.surveys = []
      state.tags = []
      state.allNextEventsLoaded = false
      state.allPreviousEventsLoaded = false
      // A held entry belongs to the conversation being left behind; committing it
      // into the next one would render a card the guest there never received.
      state.pendingGiftCardEvents = {}
      state.expectingReply = {}
    },
    RESET_STATE(state) {
      Object.assign(state, defaultState())
    },
    SET_ALL_PREVIOUS_EVENTS_LOADED(state, allPreviousEventsLoaded) {
      state.allPreviousEventsLoaded = allPreviousEventsLoaded
    },
    SET_TAGS(state, tags) {
      state.tags = tags
    },
    SET_EVENTS(state, events) {
      state.eventsById = events.reduce(
        (acc, curr) => ({
          ...acc,
          [curr._id]: curr
        }),
        state.eventsById
      )
      const newEventIds = events.map((event) => event._id)
      let rawEvents = orderGiftCardsAfterTheirReply(
        [...state.events.filter((event) => !newEventIds.includes(event._id)), ...events].sort(
          (a, b) => moment(a.created) - moment(b.created)
        )
      )

      const sortedEvents = []
      rawEvents.forEach((event) => {
        // check if the array is empty
        if (sortedEvents.length === 0) {
          const obj = {}
          Object.assign(obj, {
            timestamp: dateMapper(event.created),
            events: [event]
          })
          sortedEvents.push(obj)
        } else {
          // check if the index item exists in the array
          const index = sortedEvents.findIndex((e) => e.timestamp === dateMapper(event.created))
          if (index > -1) {
            let item = sortedEvents[index]
            item = { ...item, events: [...item.events, event] }
            // update the item at index
            sortedEvents[index] = item
          } else {
            const obj = {}
            Object.assign(obj, {
              timestamp: dateMapper(event.created),
              events: [event]
            })
            // push the new object to array
            sortedEvents.push(obj)
          }
        }
      })
      state.events = rawEvents
      state.timelineEvents = sortRatingBeforeFeedback(sortedEvents)
    },
    CLEAR_TEMP_EVENT(state, { conversationId, eventId }) {
      const update = {
        ...state.tempEvents
      }
      if (update?.[conversationId]?.[eventId]) {
        delete update[conversationId][eventId]
      }
      state.tempEvents = update
    },
    SET_TEMP_EVENTS(state, events) {
      state.tempEvents = events.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.conversation]: {
            ...(acc[curr.conversation] || {}),
            [curr._id]: curr
          }
        }),
        state.tempEvents
      )
    },
    HOLD_GIFT_CARD_EVENTS(state, { conversationId, events }) {
      const heldIds = events.map((event) => event._id)
      state.pendingGiftCardEvents = {
        ...state.pendingGiftCardEvents,
        [conversationId]: [
          ...(state.pendingGiftCardEvents[conversationId] || []).filter(
            (event) => !heldIds.includes(event._id)
          ),
          ...events
        ]
      }
    },
    CLEAR_HELD_GIFT_CARD_EVENTS(state, conversationId) {
      const update = { ...state.pendingGiftCardEvents }
      delete update[conversationId]
      state.pendingGiftCardEvents = update
    },
    SET_EXPECTING_REPLY(state, { conversationId, value }) {
      state.expectingReply = { ...state.expectingReply, [conversationId]: value }
    },
    SET_TIMELINE_LOADING(state, status) {
      state.timelineLoading = status
    },
    SET_IS_WITHIN_CELEBRATION_TIME(state, status) {
      state.isWithinCelebrationTime = status
    }
  },
  actions: {
    async clearTempEvent({ commit }, { conversationId, eventId }) {
      commit('CLEAR_TEMP_EVENT', { conversationId, eventId })
    },
    async clearTimelineData({ commit }) {
      clearAllGiftCardHoldTimers()
      commit('RESET_TIMELINE_DATA')
    },

    async resetAllTimelineData({ commit }) {
      clearAllGiftCardHoldTimers()
      commit('RESET_STATE')
    },

    async setIsWithinCelebrationTime({ commit }, status) {
      commit('SET_IS_WITHIN_CELEBRATION_TIME', status)
    },

    /**
     * `options.latest` refetches the newest page instead of paging backwards through
     * older events — the only way to surface an entry (a gift card) written after
     * load, since the default paging shape can't see it. SET_EVENTS dedupes by
     * `_id`, so re-fetching an already-rendered page is harmless.
     */
    async fetchTimeline({ commit, dispatch, getters, rootGetters }, options) {
      const { latest = false } = options || {}
      const companiesWithAccess = rootGetters['company/selectCompanies']
      commit('SET_TIMELINE_LOADING', true)
      const { selectMessageLimit, selectFirstEventId, selectAllPreviousEventsLoaded } = getters
      const selectActiveConversation = rootGetters['conversationV2/selectActiveConversation']
      if (!companiesWithAccess.some((item) => item._id == selectActiveConversation.company)) {
        router.replace('/dashboard')
        return
      }
      const initialLoad = !!!selectFirstEventId

      if (!selectActiveConversation) return
      const conversationIdBefore = selectActiveConversation._id.toString()

      if (
        selectActiveConversation.opened &&
        !selectActiveConversation.opened.includes(rootGetters['dataCore/selectActiveUserId'])
      ) {
        await dispatch(
          'conversationV2/open',
          { conversationId: selectActiveConversation._id },
          { root: true }
        )
      }

      if (selectAllPreviousEventsLoaded && !latest) {
        return
      }

      let payload = {
        filters: {
          customerIds: [selectActiveConversation.customer._id || selectActiveConversation.customer],
          companyIds: [selectActiveConversation.company],
          locationIds: rootGetters['location/selectUserActiveCompanyLocations'].map((l) => l._id)
        },
        ...(selectFirstEventId && !latest
          ? { startingAnalyticEventId: selectFirstEventId, previousLimit: selectMessageLimit }
          : { previousLimit: selectMessageLimit })
      }

      const response = await TimelineService.fetchTimeline(payload)

      if (response.body.success !== true) {
        throw response.body
      }

      // make sure conversation hasn't changed during the request wait time
      const conversationIdAfter =
        rootGetters['conversationV2/selectActiveConversation']._id.toString()
      if (conversationIdBefore !== conversationIdAfter) return

      if (response.body.data.analyticEvents.length < selectMessageLimit) {
        commit('SET_ALL_PREVIOUS_EVENTS_LOADED', true)
      } else {
        commit('SET_ALL_PREVIOUS_EVENTS_LOADED', false)
      }

      const { analyticEvents, customerOrders, surveys, messages, loyalties } = response.body.data

      // A fetched event supersedes its own placeholder, else the reply (or the ticket note)
      // renders twice until the websocket event clears it. Runs before the hold below so a
      // reply returned here already counts as real.
      analyticEvents.forEach((event) => {
        if (event.type === 'internal-note-added' && noteTicketId(event)) {
          clearTicketPlaceholderFor(commit, getters, event, conversationIdBefore)
          return
        }
        if (event.type !== 'outgoing-message' || !event.message?._id) return
        const tempEventId = `temp-${event.message._id}`
        if (getters.selectTempEventIds(conversationIdBefore).includes(tempEventId)) {
          commit('CLEAR_TEMP_EVENT', { conversationId: conversationIdBefore, eventId: tempEventId })
        }
      })

      // Same hold as the websocket path: a card entry arriving from this refetch while
      // the reply is still optimistic would render above it and then re-sort. Only
      // entries not already rendered are held, so paging older events is unaffected.
      const heldGiftCards =
        getters.selectHasOptimisticReply(conversationIdBefore) ||
        getters.selectIsExpectingReply(conversationIdBefore)
          ? analyticEvents.filter(
              (event) => event.type === 'sent-gift-card' && !getters.selectEventById(event._id)
            )
          : []

      commit(
        'SET_EVENTS',
        analyticEvents.filter((event) => !heldGiftCards.includes(event))
      )

      if (heldGiftCards.length) {
        dispatch('holdGiftCardEvents', {
          conversationId: conversationIdBefore,
          events: heldGiftCards
        })
      } else if (
        !getters.selectHasOptimisticReply(conversationIdBefore) &&
        !getters.selectIsExpectingReply(conversationIdBefore)
      ) {
        // The reply is real as of this batch, so an entry held earlier — a card whose
        // websocket event won the race — goes in underneath it now rather than waiting
        // on a websocket event that has already been superseded.
        dispatch('commitHeldGiftCardEvents', { conversationId: conversationIdBefore })
      }

      // dispatch('fetchTags') // this will get implemented with the customer details panel

      if (initialLoad) {
        dispatch('scrollToLastEvent')
      }

      commit('SET_TIMELINE_LOADING', false)
    },

    async scrollToLastEvent({ getters }) {
      scrollToElement(`timeline-${getters.selectLastEventId}`)
    },

    setIsWithinCelebrationTime({ commit }, status) {
      commit('SET_IS_WITHIN_CELEBRATION_TIME', status)
    },

    async setTempTimelineEvent({ commit }, event) {
      commit('SET_TEMP_EVENTS', [event])
    },

    // Cleared by the real note in appendTimelineEvent or fetchTimeline; the timer covers a
    // note that never arrives.
    setTicketPlaceholder({ commit, dispatch, state }, event) {
      if (hasRealTicketNote(state.events, noteTicketId(event))) return
      commit('SET_TEMP_EVENTS', [event])
      const conversationId = event.conversation
      const eventId = event._id
      clearTicketPlaceholderTimer(eventId)
      ticketPlaceholderTimers[eventId] = setTimeout(() => {
        dispatch('expireTicketPlaceholder', { conversationId, eventId })
      }, TICKET_PLACEHOLDER_TIMEOUT_MS)
    },

    // Refetch before dropping, so a note whose websocket event was lost swaps in without the
    // bubble vanishing first. fetchTimeline reads the active conversation, hence the check.
    async expireTicketPlaceholder(
      { commit, dispatch, getters, rootGetters },
      { conversationId, eventId }
    ) {
      clearTicketPlaceholderTimer(eventId)
      if (!getters.selectTempEventIds(conversationId).includes(eventId)) return
      try {
        const activeConversation = rootGetters['conversationV2/selectActiveConversation']
        if (activeConversation && activeConversation._id === conversationId) {
          await dispatch('fetchTimeline', { latest: true })
        }
      } catch (error) {
        // fetchTimeline leaves the loading flag set when it throws, which blocks paging back.
        commit('SET_TIMELINE_LOADING', false)
        console.error('Failed to refetch the timeline for a ticket placeholder', error?.status)
      } finally {
        commit('CLEAR_TEMP_EVENT', { conversationId, eventId })
      }
    },

    setExpectingReply({ commit }, { conversationId, value }) {
      commit('SET_EXPECTING_REPLY', { conversationId, value })
    },

    /**
     * Holds a card's timeline entry back until the reply that introduces it is real —
     * committing it earlier shows it above the optimistic reply, then jumps below once
     * that reply lands. The timer is the escape hatch if the real event never arrives.
     */
    holdGiftCardEvents({ commit, dispatch }, { conversationId, events }) {
      if (!conversationId || !events?.length) return
      commit('HOLD_GIFT_CARD_EVENTS', { conversationId, events })

      if (giftCardHoldTimers[conversationId]) return
      giftCardHoldTimers[conversationId] = setTimeout(() => {
        dispatch('commitHeldGiftCardEvents', { conversationId })
      }, GIFT_CARD_HOLD_TIMEOUT_MS)
    },

    commitHeldGiftCardEvents({ commit, getters }, { conversationId }) {
      if (giftCardHoldTimers[conversationId]) {
        clearTimeout(giftCardHoldTimers[conversationId])
        delete giftCardHoldTimers[conversationId]
      }

      const held = getters.selectPendingGiftCardEvents(conversationId)
      if (!held.length) return

      commit('CLEAR_HELD_GIFT_CARD_EVENTS', conversationId)
      commit('SET_EVENTS', held)
      scrollToElement(`timeline-${getters.selectLastEventId}`)
    },

    async processTimelineEventAdded({ dispatch, rootGetters }, analyticEvent) {
      const activeConversation = rootGetters['conversationV2/selectActiveConversation']
      if (!activeConversation || !analyticEvent || analyticEvent.type === 'order') {
        return
      }

      const activeConversationId = activeConversation._id
      const { conversation, company, location, customer } = analyticEvent
      const eventCustomerId = customer?._id
      const conversationCustomerId = activeConversation?.customer?._id
      const eventLocationId = location?._id
      const conversationLocationId = activeConversation?.location?._id
      const eventCompanyId = company
      const conversationCompanyId = activeConversation.company
      const eventConversationId = conversation

      if (
        eventConversationId === activeConversationId ||
        (conversationCustomerId === eventCustomerId &&
          eventCompanyId === conversationCompanyId &&
          (!eventLocationId || eventLocationId === conversationLocationId))
      ) {
        dispatch('appendTimelineEvent', { analyticEvent, conversation: activeConversation })
      }
    },

    appendTimelineEvent({ commit, dispatch, getters }, { analyticEvent, conversation }) {
      if (analyticEvent.type === 'outgoing-message') {
        const tempEventId = `temp-${analyticEvent.message?._id}`
        if (
          analyticEvent?.message?._id &&
          getters.selectTempEventIds(conversation._id).includes(tempEventId)
        ) {
          commit('CLEAR_TEMP_EVENT', { conversationId: conversation._id, eventId: tempEventId })
        }
      }
      if (analyticEvent.type === 'internal-note-added') {
        const tempEventId = `temp-${analyticEvent.note?._id}`
        if (
          analyticEvent?.note?._id &&
          getters.selectTempEventIds(conversation._id).includes(tempEventId)
        ) {
          commit('CLEAR_TEMP_EVENT', { conversationId: conversation._id, eventId: tempEventId })
        }
      }
      if (analyticEvent.type === 'internal-note-added' && noteTicketId(analyticEvent)) {
        clearTicketPlaceholderFor(commit, getters, analyticEvent, conversation._id)
      }
      // Nothing renderable in an entry whose card is still a bare id — committing it
      // paints a bubble with the digits and amount missing, which then fills in on the
      // next fetch. Refetch for the hydrated copy and render only that.
      if (analyticEvent.type === 'sent-gift-card' && !isGiftCardEventHydrated(analyticEvent)) {
        throttleGiftCardHydration(dispatch)
        return
      }
      if (analyticEvent.type === 'sent-gift-card') {
        const tempEventId = `temp-${analyticEvent.giftCard?._id}`
        if (
          analyticEvent?.giftCard?._id &&
          getters.selectTempEventIds(conversation._id).includes(tempEventId)
        ) {
          commit('CLEAR_TEMP_EVENT', { conversationId: conversation._id, eventId: tempEventId })
        }
      }
      // Held rather than committed: the reply it belongs under is still optimistic,
      // and the optimistic block renders after every real event, so committing now
      // puts the card above the operator's own message. Runs after the placeholder
      // cleanup above so nothing is left behind in `tempEvents`.
      if (
        analyticEvent.type === 'sent-gift-card' &&
        (getters.selectHasOptimisticReply(conversation._id) ||
          getters.selectIsExpectingReply(conversation._id))
      ) {
        dispatch('holdGiftCardEvents', {
          conversationId: conversation._id,
          events: [analyticEvent]
        })
        return
      }

      commit('SET_EVENTS', [analyticEvent])

      // The reply is real now, so anything held for it can be committed underneath.
      if (analyticEvent.type === 'outgoing-message') {
        dispatch('commitHeldGiftCardEvents', { conversationId: conversation._id })
      }

      scrollToElement(`timeline-${getters.selectLastEventId}`)
    }
  }
}
